import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { subSeconds, formatISO } from "date-fns";

import { useChatStore } from "@stores/chat.store";
import { useAdvertiserStore } from "@stores/advertiser";
import { useChatMessages } from "@hooks/api/chat-websocket";
import { useAnalytics } from "@hooks/analytics";

import { getConversationType } from "@/utils/helpers";
import { CHAT_MESSAGE_STATUS } from "@/utils/constants";
import { WS_TIME_DELAY, containsSensitiveData, removeSensitiveData } from "./utils/sender.constants";

import type { TSenderLogicConfig, TSenderLogicResult } from "./sender.types";

/** Gerencia input de mensagem, validação de dados sensíveis (telefone/email) e envio via WebSocket. */
export const useSenderLogic = ({
  sensitiveDataWarning,
  toggleSensitiveDataWarning,
}: TSenderLogicConfig): TSenderLogicResult => {
  const conversationActive = useChatStore((s) => s.conversation.active);
  const conversationChatType = useChatStore((s) => s.conversation.chatType);
  const storeTabsConversation = useChatStore((s) => s.tabs.conversation);
  const advertiserSource = useAdvertiserStore((s) => s.source);
  const { sendMessage, updateActiveConversation } = useChatMessages();
  const { trackClick } = useAnalytics();

  const [inputValue, setInputValue] = useState("");

  const disabled = sensitiveDataWarning.show || !inputValue.trim();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setInputValue(event.target.value.trim() ? event.target.value : ""),
    [],
  );

  const checkContentOfMessage = useCallback(
    () => containsSensitiveData(inputValue),
    [inputValue],
  );
  const removePhonesAndEmails = () =>
    setInputValue(removeSensitiveData(inputValue));

  const submitMessage = useCallback(() => {
    const { Retorno } = advertiserSource ?? {};
    const userNameFiltered = conversationActive;
    const info = userNameFiltered?.message;
    const chatId = userNameFiltered?.chatId;
    const date = subSeconds(new Date(), WS_TIME_DELAY.seconds);
    const source = {
      ...info,
      message: inputValue,
      sentOn: formatISO(date),
      senderId: `PF:${Retorno ? Retorno.Id : 0}`,
      statusMessage: CHAT_MESSAGE_STATUS.sending,
    };

    sendMessage(inputValue, conversationActive?.chatId ?? "");
    updateActiveConversation({
      source,
      chatId: chatId ?? "",
      chatType: conversationChatType ?? '',
    });
    setInputValue("");
  }, [
    inputValue,
    advertiserSource,
    conversationActive,
    conversationChatType,
    sendMessage,
    updateActiveConversation,
  ]);

  const submitMessageRef = useRef(submitMessage);
  const removePhonesAndEmailsRef = useRef(removePhonesAndEmails);

  useEffect(() => {
    submitMessageRef.current = submitMessage;
    removePhonesAndEmailsRef.current = removePhonesAndEmails;
  });

  useEffect(() => {
    if (sensitiveDataWarning.accepted === true) {
      submitMessageRef.current();
      toggleSensitiveDataWarning(false, undefined);
    } else if (sensitiveDataWarning.accepted === false) {
      removePhonesAndEmailsRef.current();
      toggleSensitiveDataWarning(false, undefined);
    }
  }, [sensitiveDataWarning.accepted, toggleSensitiveDataWarning]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      trackClick({
        eventName: 'clickGaragemEnviarMensagem',
        pageName: '/webmotors/garagem/chat',
        pageType: 'chat',
        flowType: 'garagem',
        tier: 3,
        recurringEvent: true,
      });

      if (!checkContentOfMessage() && sensitiveDataWarning.accepted === undefined) {
        submitMessage();
      } else {
        toggleSensitiveDataWarning(true, undefined);
      }
    },
    [
      sensitiveDataWarning,
      checkContentOfMessage,
      submitMessage,
      toggleSensitiveDataWarning,
      trackClick,
    ],
  );

  const handleSelectMessage = ({ message }: { message: string }) => {
    setInputValue(message);
  };

  const conversationType = getConversationType(storeTabsConversation);

  return {
    disabled,
    inputValue,
    handleChange,
    handleSubmit,
    handleSelectMessage,
    conversationType,
  };
};
